import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, WifiOff, AlertTriangle } from 'lucide-react';
import { mcpService, MCPStatus } from '../services/mcpService';

interface MCPStatusIndicatorProps {
    className?: string;
}

const MCPStatusIndicator: React.FC<MCPStatusIndicatorProps> = ({ className = '' }) => {
    const [status, setStatus] = useState<MCPStatus>(mcpService.getStatus());

    useEffect(() => {
        const unsubscribe = mcpService.subscribe(setStatus);
        return unsubscribe;
    }, []);

    const getStatusConfig = () => {
        if (!status.connected) {
            return {
                icon: WifiOff,
                color: 'var(--accent-danger)',
                bgColor: 'rgba(248, 113, 113, 0.1)',
                label: 'MCP Offline',
                pulse: false
            };
        }

        if (status.serverHealth === 'degraded') {
            return {
                icon: AlertTriangle,
                color: 'var(--accent-alert)',
                bgColor: 'rgba(245, 158, 11, 0.1)',
                label: `MCP Slow (${status.latency}ms)`,
                pulse: true
            };
        }

        return {
            icon: Activity,
            color: 'var(--accent-safe)',
            bgColor: 'rgba(16, 185, 129, 0.1)',
            label: `MCP Online (${status.latency}ms)`,
            pulse: true
        };
    };

    const config = getStatusConfig();
    const Icon = config.icon;

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl ${className}`}
            style={{
                backgroundColor: config.bgColor,
                border: `1px solid ${config.color}40`
            }}
        >
            {/* Pulse animation for active states */}
            {config.pulse && (
                <motion.div
                    className="absolute inset-0 rounded-xl"
                    style={{ borderColor: config.color, borderWidth: 1 }}
                    animate={{
                        scale: [1, 1.05, 1],
                        opacity: [0.5, 0.2, 0.5]
                    }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
            )}

            {/* Icon */}
            <div className="relative z-10 flex items-center justify-center">
                <Icon
                    className="w-4 h-4"
                    style={{ color: config.color }}
                    strokeWidth={2.5}
                />
                {config.pulse && (
                    <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ backgroundColor: config.color }}
                        animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    />
                )}
            </div>

            {/* Label */}
            <span
                className="text-xs font-semibold relative z-10"
                style={{ color: config.color }}
            >
                {config.label}
            </span>
        </motion.div>
    );
};

export default MCPStatusIndicator;
